import React, { useEffect, useMemo, useState } from "react";

import { useSearchParams, useNavigate } from "react-router-dom";
import { CommonDashboardWrapper } from "styles/GlobalStyles";
import { XCircleIcon } from "@phosphor-icons/react";

import {
  MagnifyingGlassOutlineRegularIcon,
  RightIconOutlineRegularIcon,
  XOutlineRegularIcon
} from "assets/SVGComponents";

import Input from "components/Input";
import Select from "components/Select/index";
import CommonTable from "components/Tables/CommonTable";
import columns from "./columns";
import { infoOptions, searchFieldOptions, subscriptionOptions } from "./data";

import { useDispatch, useSelector } from "react-redux";
import { useCreatorUsersSlice } from "./slice/index";
import { getCreatorUsersInfo } from "./slice/selectors";
import { ContainerWrapper, TableWrapperStyles } from "pages/Users/styled";
import PillButtons from "components/Buttons/PillButtons";
import Icon from "components/Icon/Icon";
import CreatorSubscriptionModal from "./Modals/CreatorSubscriptionModal";

const CreatorUsers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { actions } = useCreatorUsersSlice();
  const {
    loading,
    users,
    pagination,
    status_counts,
    subscription_counts,
    creator_plans
  } = useSelector(getCreatorUsersInfo);

  const [isInitialized, setIsInitialized] = useState(false);
  const [userFilters, setUserFilters] = useState({
    search_field: "phone_number",
    status: null,
    subscription: null
  });
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPlanHistoryModal, setShowPlanHistoryModal] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);

  const page = Number(searchParams.get("page")) || 1;

  const statusMenuItems = infoOptions.map((option) => ({
    label: option.label,
    value: option.apiKey,
    count: status_counts?.[option.apiKey] || 0
  }));

  const subscriptionMenuItems = useMemo(() => {
    return (creator_plans || []).map((plan) => ({
      label: plan.name,
      value: plan.name,
      count: subscription_counts?.[plan.name] ?? 0
    }));
  }, [creator_plans, subscription_counts]);

  const handleViewSubscription = (creator) => {
    setSelectedCreator(creator);
    setShowPlanHistoryModal(true);
  };

  const tableColumns = useMemo(
    () => columns(navigate, searchParams, handleViewSubscription),
    [navigate, searchParams]
  );

  /* ---------------------------
     Read Search & Filters from URL on mount
  ----------------------------*/
  useEffect(() => {
    if (!creator_plans || isInitialized) return;

    const search = searchParams.get("search") || "";
    const searchField = searchParams.get("search_field") || "phone_number";
    const status = searchParams.get("status") || null;
    const subscription = searchParams.get("subscription") || null;

    setSearchTerm(search);
    setDebouncedSearch(search);
    setUserFilters({
      search_field: searchField,
      status:
        infoOptions
          .map((o) => ({ label: o.label, value: o.apiKey }))
          .find((s) => s.value === status) || null,

      subscription:
        creator_plans
          ?.map((plan) => ({
            label: plan.name,
            value: plan.name
          }))
          .find((item) => item.value === subscription) || null
    });

    setIsInitialized(true);
  }, [creator_plans]);

  /* ---------------------------
     Update URL when filters change
  ----------------------------*/
  useEffect(() => {
    if (!isInitialized) return;

    const params = new URLSearchParams(searchParams.toString());
    const currentSearch = searchParams.get("search") || "";
    const currentStatus = searchParams.get("status") || "";
    const currentSubscription = searchParams.get("subscription") || "";

    // Reset page if search OR status changed
    if (
      debouncedSearch !== currentSearch ||
      (userFilters.status?.value || "") !== currentStatus ||
      (userFilters.subscription?.value || "") !== currentSubscription
    ) {
      params.set("page", "1");
    }

    if (debouncedSearch) {
      params.set("search", debouncedSearch);
    } else {
      params.delete("search");
    }

    if (userFilters.search_field) {
      params.set("search_field", userFilters.search_field);
    }

    if (userFilters.status?.value) {
      params.set("status", userFilters.status.value);
    } else {
      params.delete("status");
    }

    if (userFilters.subscription?.value) {
      params.set("subscription", userFilters.subscription.value);
    } else {
      params.delete("subscription");
    }

    setSearchParams(params);
  }, [debouncedSearch, userFilters, isInitialized]);

  /* ---------------------------
     Fetch users on filter/search/page change
  ----------------------------*/
  useEffect(() => {
    if (!isInitialized) return;
    handleUserFetch(page);
  }, [debouncedSearch, userFilters, isInitialized, page]);

  /* ---------------------------
     Debounce Search
  ----------------------------*/
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleUserFetch = (page = 1) => {
    dispatch(
      actions.fetchCreatorUsersRequest({
        search: debouncedSearch || "",
        search_field: userFilters.search_field || "phone_number",
        status: userFilters.status?.value || "",
        subscription: userFilters.subscription?.value || "",
        page,
        limit: 10
      })
    );
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage);
    setSearchParams(params);
  };

  return (
    <>
      <ContainerWrapper>
        <CommonDashboardWrapper>
          <div className="common-dashboard-wrapper">
            <h1 className="common-heading">All Users (Creators)</h1>
          </div>

          <TableWrapperStyles>
            <div className="filter-bar">
              <div className="filter-bar-menus">
                <PillButtons
                  placeholderText="Account Status"
                  statusPlaceholder="statusActive"
                  mainLabelText={userFilters.status?.label}
                  rightIcon={<RightIconOutlineRegularIcon />}
                  onSelect={(item) => {
                    setUserFilters((prev) => ({ ...prev, status: item }));
                  }}
                  menuItems={statusMenuItems}
                  size="M"
                  maxWidth="max-content"
                  customClassName="status-width"
                  activeRightIcon={
                    <Icon
                      component={XCircleIcon}
                      format="outline"
                      weight="regular"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUserFilters((prev) => ({ ...prev, status: null }));
                      }}
                    />
                  }
                />

                <PillButtons
                  placeholderText="Choose Subscription"
                  statusPlaceholder="statusActive"
                  mainLabelText={userFilters.subscription?.label}
                  rightIcon={<RightIconOutlineRegularIcon />}
                  onSelect={(item) => {
                    setUserFilters((prev) => ({
                      ...prev,
                      subscription: item
                    }));
                  }}
                  menuItems={subscriptionMenuItems}
                  size="M"
                  maxWidth="max-content"
                  customClassName="status-width"
                  activeRightIcon={
                    <Icon
                      component={XCircleIcon}
                      format="outline"
                      weight="regular"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUserFilters((prev) => ({
                          ...prev,
                          subscription: null
                        }));
                      }}
                    />
                  }
                />
              </div>

              <div className="search-dropdown-styles">
                <Select
                  size="S"
                  selectWrapperClassName="search-select"
                  options={searchFieldOptions}
                  value={userFilters.search_field}
                  onChange={(option) =>
                    setUserFilters((prev) => ({
                      ...prev,
                      search_field: option
                    }))
                  }
                  placeholder="Search by"
                />
                <Input
                  id="searchUser"
                  type="search"
                  placeholder="Search for Phone"
                  leftIcon={<MagnifyingGlassOutlineRegularIcon />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="S"
                  className="search-input"
                  rightIcon={<XOutlineRegularIcon />}
                  handleClearSearch={() => setSearchTerm("")}
                />
              </div>
            </div>

            <CommonTable
              data={users}
              columns={tableColumns}
              total_count={pagination?.total}
              loading={loading}
              initialLoading={loading}
              page={page}
              limit={pagination?.limit || 10}
              tableClassName="comman-tabel-view"
              tableWrapperStyles="table-wrapper-styles"
              onPageChange={handlePageChange}
            />
          </TableWrapperStyles>
        </CommonDashboardWrapper>
      </ContainerWrapper>

      <CreatorSubscriptionModal
        show={showPlanHistoryModal}
        creator={selectedCreator}
        onHide={() => {
          setShowPlanHistoryModal(false);
          setSelectedCreator(null);
        }}
        searchParams={searchParams}
      />
    </>
  );
};

export default CreatorUsers;
